import * as contentful from 'contentful'
import { cache } from 'services'

import { captureException } from '../../error-tracking'

export default class DefaultClient {
    host: string
    space: string
    accessToken: string
    availableLocales: string[]
    isPreview: boolean
    cacheKey: string
    cache: {
        get: (k: string) => any
        set: (k: string, v: any) => void
    }
    client: contentful.ContentfulClientApi

    constructor(
        cacheKey = cache.keys.DEFAULT_CONTENTFUL_CLIENT,
        {
            /**
             * Default space config is 'Doist Contentful Space' at:
             * https://app.contentful.com/spaces/dm4oa8qtogq0/home
             */
            host = process.env.DOIST_CONTENTFUL_HOST,
            space = process.env.DOIST_CONTENTFUL_SPACE_ID,
            accessToken = process.env.DOIST_CONTENTFUL_ACCESS_TOKEN,
            availableLocales = ['en', 'es', 'fr', 'nl', 'zh-CN', 'it', 'de', 'pt', 'ja'],
        } = {},
    ) {
        this.host = host
        this.space = space
        this.accessToken = accessToken
        this.availableLocales = availableLocales /** set on contentful doist space */
        this.isPreview = this.host?.startsWith('preview.')

        /** cache with specific key_prefixes */
        this.cacheKey = cacheKey
        this.cache = {
            get: (key: string) => cache.get(`${this.cacheKey}_${key}`),
            set: (key: string, val: any) => cache.set(`${this.cacheKey}_${key}`, val),
        }

        /** init client */
        this.client = contentful.createClient({ space, host, accessToken })
    }

    async queryGraphQL(query: string) {
        /**
         * You can preview query results at GraphiQL instance here:
         * https://graphql.contentful.com/content/v1/spaces/{SPACE_ID}/explore?access_token={CDA_TOKEN}
         */
        const graphQLPath = `https://graphql.contentful.com/content/v1/spaces/${this.space}`
        try {
            const abortController = new AbortController()
            const timeoutId = setTimeout(() => abortController.abort(), 3000)
            const res = await fetch(graphQLPath, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json',
                    authorization: `Bearer ${this.accessToken}`,
                },
                body: JSON.stringify({ query }),
                signal: abortController.signal,
            })
            clearTimeout(timeoutId)
            const { data, errors } = await res.json()
            if (errors) {
                // mostly likely due to draft content not being resolvable.
                // This means that some fields will return null, but should
                // be handled by the components that deal with the data.
                console.error(errors)
            }
            return data
        } catch (error) {
            captureException(error)
            throw error
        }
    }

    getLocale(lang: string) {
        if (lang === 'pt-BR') lang = 'pt'

        if (this.availableLocales.includes(lang)) {
            return lang
        } else {
            return 'en'
        }
    }
}
